import type { ExerciseDef } from './types'

// ─── 2.3 Algebra ──────────────────────────────────────────────────────────────

export const orderOfOps: ExerciseDef = {
  starter: `// Multiplication and division happen before + and − …
console.log(2 + 3 * 4)        // 14, not 20
// … unless brackets say otherwise. Brackets always go first.
console.log((2 + 3) * 4)      // 20
// Powers go before multiplication:
console.log(2 * 3 ** 2)       // 2 × 9 = 18

// A fraction is just a division waiting to happen:
console.log(3 / 4, 1 / 3)
// Percentages are fractions of 100:  15% of 80
console.log(0.15 * 80)

// Formulas in ML are written the same way. Spot the brackets:
const w = 2, x = 3, b = 1
console.log(w * x + b)        // (w·x) + b = 7
console.log(w * (x + b))      // a different formula = 8
`,
}

export const solveLinear: ExerciseDef = {
  starter: `// 1. solve(a, b, c): the x that makes  a·x + b = c  true.
//    Undo the steps in reverse order: take away b, then divide by a.
function solve(a: number, b: number, c: number): number {
  return 0
}

// 2. toCelsius(f): the formula f = 1.8·c + 32 rearranged to give c.
function toCelsius(f: number): number {
  return 0
}

// 3. meet(m1, b1, m2, b2): the x where  m1·x + b1 = m2·x + b2.
//    Move the x's to one side and the plain numbers to the other.
function meet(m1: number, b1: number, m2: number, b2: number): number {
  return 0
}

console.log(solve(3, 2, 14), toCelsius(212), meet(2, 1, -1, 7))
`,
  solution: `function solve(a: number, b: number, c: number): number {
  return (c - b) / a
}

function toCelsius(f: number): number {
  return (f - 32) / 1.8
}

function meet(m1: number, b1: number, m2: number, b2: number): number {
  return (b2 - b1) / (m1 - m2)
}

console.log(solve(3, 2, 14), toCelsius(212), meet(2, 1, -1, 7))
`,
  tests: `
test("3x + 2 = 14  →  x = 4", () => expect(solve(3, 2, 14), "solve(3, 2, 14)").toBeCloseTo(4))
test("2x − 6 = 0  →  x = 3", () => expect(solve(2, -6, 0), "solve(2, -6, 0)").toBeCloseTo(3))
test("212 °F is 100 °C", () => expect(toCelsius(212), "toCelsius(212)").toBeCloseTo(100))
test("32 °F is 0 °C", () => expect(toCelsius(32), "toCelsius(32)").toBeCloseTo(0))
test("2x + 1 and −x + 7 meet at x = 2", () => expect(meet(2, 1, -1, 7), "meet(2, 1, -1, 7)").toBeCloseTo(2))
test("check by plugging back in", () => {
  const __x = meet(0.5, 3, -2, 8)
  expect(0.5 * __x + 3, "left side at that x").toBeCloseTo(-2 * __x + 8)
})
`,
}

export const bisection: ExerciseDef = {
  starter: `// Some equations can't be rearranged (try x³ + x = 10!).
// So we SEARCH: if f(mid) is too small, the answer is to the right; else to the left.
// Each step halves the range. This is called bisection.

// solveBySearch(f, target, lo, hi): an x between lo and hi with f(x) ≈ target.
// Assume f only ever goes up (f(lo) < target < f(hi)). Do 60 halvings.
function solveBySearch(f: (x: number) => number, target: number, lo: number, hi: number): number {
  for (let step = 0; step < 60; step++) {
    const mid = (lo + hi) / 2
    // TODO: if f(mid) is below the target, move lo up to mid — otherwise move hi down to mid
  }
  return (lo + hi) / 2
}

console.log(solveBySearch(x => x * x, 2, 0, 2))          // √2 ≈ 1.41421
console.log(solveBySearch(x => x ** 3 + x, 10, 0, 5))    // ≈ 2
`,
  solution: `function solveBySearch(f: (x: number) => number, target: number, lo: number, hi: number): number {
  for (let step = 0; step < 60; step++) {
    const mid = (lo + hi) / 2
    if (f(mid) < target) lo = mid
    else hi = mid
  }
  return (lo + hi) / 2
}

console.log(solveBySearch(x => x * x, 2, 0, 2))
console.log(solveBySearch(x => x ** 3 + x, 10, 0, 5))
`,
  tests: `
test("finds √2 (x² = 2)", () => expect(solveBySearch(x => x * x, 2, 0, 2), "solveBySearch(x => x * x, 2, 0, 2)").toBeCloseTo(Math.SQRT2))
test("solves x³ + x = 10", () => expect(solveBySearch(x => x ** 3 + x, 10, 0, 5), "x").toBeCloseTo(2))
test("solves 2ˣ = 1000", () => expect(solveBySearch(x => 2 ** x, 1000, 0, 20), "x").toBeCloseTo(Math.log2(1000)))
`,
}

// ─── 2.6 Big & small numbers ──────────────────────────────────────────────────

export const floats: ExerciseDef = {
  starter: `// Scientific notation in code: the number after "e" says how many places to move the point.
console.log(1.5e9)            // 1 500 000 000 — GPT-2's parameter count
console.log(3e-4)             // 0.0003 — a typical learning rate

// Computers store most numbers approximately:
console.log(0.1 + 0.2)              // not quite 0.3!
console.log(0.1 + 0.2 === 0.3)      // false
console.log(Math.abs(0.1 + 0.2 - 0.3) < 1e-9)   // compare with a tolerance instead

// There are limits at both ends:
console.log(Math.exp(709))    // about 8e307 — near the biggest number there is
console.log(Math.exp(710))    // too big → Infinity
console.log(Math.exp(-746))   // too small → 0 ("underflow")
console.log(Infinity - Infinity)    // NaN: "not a number"

// Fewer bits = fewer digits survive. Math.fround stores a number in 32 bits:
console.log(Math.fround(0.1))
`,
}

export const sciNotation: ExerciseDef = {
  starter: `// 1. approxEqual(a, b, tol): are a and b within tol of each other? (tol defaults to 1e-9)
function approxEqual(a: number, b: number, tol = 1e-9): boolean {
  return a === b
}

// 2. toSci(x): split a positive number into [mantissa, exponent] so that
//    x = mantissa × 10^exponent and 1 ≤ mantissa < 10.
//    toSci(1500) → [1.5, 3]     toSci(0.00042) → [4.2, -4]
//    Hint: the exponent is Math.floor(Math.log10(x)).
function toSci(x: number): [number, number] {
  return [x, 0]
}

console.log(approxEqual(0.1 + 0.2, 0.3), toSci(1500), toSci(0.00042))
`,
  solution: `function approxEqual(a: number, b: number, tol = 1e-9): boolean {
  return Math.abs(a - b) <= tol
}

function toSci(x: number): [number, number] {
  const e = Math.floor(Math.log10(x))
  return [x / 10 ** e, e]
}

console.log(approxEqual(0.1 + 0.2, 0.3), toSci(1500), toSci(0.00042))
`,
  tests: `
test("0.1 + 0.2 is approximately 0.3", () => expect(approxEqual(0.1 + 0.2, 0.3), "approxEqual(0.1 + 0.2, 0.3)").toBe(true))
test("1 and 1.1 are not approximately equal", () => expect(approxEqual(1, 1.1), "approxEqual(1, 1.1)").toBe(false))
test("a looser tolerance accepts them", () => expect(approxEqual(1, 1.1, 0.2), "approxEqual(1, 1.1, 0.2)").toBe(true))
test("toSci(1500) is [1.5, 3]", () => {
  const [__m, __e] = toSci(1500)
  expect(__m, "mantissa").toBeCloseTo(1.5)
  expect(__e, "exponent").toBe(3)
})
test("toSci(0.00042) is [4.2, -4]", () => {
  const [__m, __e] = toSci(0.00042)
  expect(__m, "mantissa").toBeCloseTo(4.2)
  expect(__e, "exponent").toBe(-4)
})
test("toSci(7) is [7, 0]", () => expect(toSci(7)[1], "exponent of 7").toBe(0))
`,
}

export const stableMath: ExerciseDef = {
  starter: `// Two tricks every ML library uses to dodge overflow and underflow.

// 1. share(a, b) = eᵃ / (eᵃ + eᵇ): how much of the total "a" gets.
//    For a = 1000 the naive version computes Infinity / Infinity = NaN.
//    Trick: divide top and bottom by eᵃ  →  1 / (1 + e^(b − a)). Same value, no overflow.
function share(a: number, b: number): number {
  return Math.exp(a) / (Math.exp(a) + Math.exp(b))
}

// 2. logProduct(ps): the log of p₀ × p₁ × p₂ × …
//    Multiplying 500 small probabilities underflows to 0. But log(a × b) = log a + log b,
//    so add up the logs instead.
function logProduct(ps: number[]): number {
  let product = 1
  for (const p of ps) product *= p
  return Math.log(product)
}

console.log(share(1000, 999), logProduct(new Array(500).fill(0.01)))
`,
  solution: `function share(a: number, b: number): number {
  return 1 / (1 + Math.exp(b - a))
}

function logProduct(ps: number[]): number {
  let total = 0
  for (const p of ps) total += Math.log(p)
  return total
}

console.log(share(1000, 999), logProduct(new Array(500).fill(0.01)))
`,
  tests: `
test("share(0, 0) is 0.5", () => expect(share(0, 0), "share(0, 0)").toBeCloseTo(0.5))
test("share(2, 1) ≈ 0.731", () => expect(share(2, 1), "share(2, 1)").toBeCloseTo(0.7310586, 1e-6))
test("share(1000, 999) doesn't overflow", () => expect(share(1000, 999), "share(1000, 999)").toBeCloseTo(0.7310586, 1e-6))
test("logProduct([0.5, 0.5]) = log(0.25)", () => expect(logProduct([0.5, 0.5]), "logProduct([0.5, 0.5])").toBeCloseTo(Math.log(0.25)))
test("500 probabilities of 0.01 don't underflow", () => expect(logProduct(new Array(500).fill(0.01)), "logProduct(500 × 0.01)").toBeCloseTo(500 * Math.log(0.01), 1e-6))
`,
}

// ─── 2.7 Trigonometry ─────────────────────────────────────────────────────────

export const trigBasics: ExerciseDef = {
  starter: `// JavaScript measures angles in radians: a full turn is 2π (about 6.283).
console.log(Math.PI)                       // half a turn
const deg = 60
const rad = deg * Math.PI / 180            // degrees → radians
console.log(rad)

console.log(Math.cos(rad), Math.sin(rad))  // the point at 60° on the unit circle
console.log(Math.cos(rad) ** 2 + Math.sin(rad) ** 2)   // always 1 — it's on the circle!

// atan2(y, x) goes the other way: from a point back to its angle.
console.log(Math.atan2(1, 1) * 180 / Math.PI)   // 45°

plot.fn([Math.sin, Math.cos], [0, 2 * Math.PI], { title: "sin and cos over one full turn" })
`,
}

export const rotation: ExerciseDef = {
  starter: `// 1. toRadians(deg)
function toRadians(deg: number): number {
  return 0
}

// 2. pointOnCircle(angle, r): the point at this angle (radians) on a circle of radius r.
function pointOnCircle(angle: number, r: number): [number, number] {
  return [0, 0]
}

// 3. rotate([x, y], angle): turn a point around the origin by "angle" radians:
//    x' = x·cos(angle) − y·sin(angle)
//    y' = x·sin(angle) + y·cos(angle)
function rotate(p: [number, number], angle: number): [number, number] {
  return p
}

console.log(toRadians(180), pointOnCircle(toRadians(90), 2), rotate([1, 0], toRadians(90)))
`,
  solution: `function toRadians(deg: number): number {
  return deg * Math.PI / 180
}

function pointOnCircle(angle: number, r: number): [number, number] {
  return [r * Math.cos(angle), r * Math.sin(angle)]
}

function rotate(p: [number, number], angle: number): [number, number] {
  const [x, y] = p
  return [x * Math.cos(angle) - y * Math.sin(angle), x * Math.sin(angle) + y * Math.cos(angle)]
}

console.log(toRadians(180), pointOnCircle(toRadians(90), 2), rotate([1, 0], toRadians(90)))
`,
  tests: `
test("180° is π radians", () => expect(toRadians(180), "toRadians(180)").toBeCloseTo(Math.PI))
test("the point at 90° on a circle of radius 2 is (0, 2)", () => {
  const [__x, __y] = pointOnCircle(Math.PI / 2, 2)
  expect(__x, "x").toBeCloseTo(0)
  expect(__y, "y").toBeCloseTo(2)
})
test("rotating (1, 0) by 90° gives (0, 1)", () => {
  const [__x, __y] = rotate([1, 0], Math.PI / 2)
  expect(__x, "x").toBeCloseTo(0)
  expect(__y, "y").toBeCloseTo(1)
})
test("rotating keeps the distance from the origin", () => {
  const [__x, __y] = rotate([3, 4], 1.234)
  expect(Math.hypot(__x, __y), "distance after rotating (3, 4)").toBeCloseTo(5)
})
test("rotating by a then b is the same as rotating by a + b", () => {
  const __p = rotate(rotate([2, 1], 0.4), 0.9)
  const __q = rotate([2, 1], 1.3)
  expect(__p[0], "x").toBeCloseTo(__q[0])
  expect(__p[1], "y").toBeCloseTo(__q[1])
})
`,
}

export const positionCode: ExerciseDef = {
  starter: `// Transformers need to know WHERE each word is. One famous trick gives every position
// a fingerprint made of waves: slow waves and fast waves, sin and cos.
//
// encodePosition(pos, dims): an array of "dims" numbers (dims is even). For i = 0, 1, …, dims/2 − 1:
//   freq = 1 / 10000^(2i / dims)
//   out[2i]     = sin(pos × freq)
//   out[2i + 1] = cos(pos × freq)
function encodePosition(pos: number, dims: number): number[] {
  const out: number[] = []
  return out
}

console.log(encodePosition(0, 4), encodePosition(1, 4))
plot.heatmap(range(32).map(p => encodePosition(p, 16)), { title: "positions 0–31 (rows) × 16 wave values (columns)" })
`,
  solution: `function encodePosition(pos: number, dims: number): number[] {
  const out: number[] = []
  for (let i = 0; i < dims / 2; i++) {
    const freq = 1 / 10000 ** ((2 * i) / dims)
    out.push(Math.sin(pos * freq), Math.cos(pos * freq))
  }
  return out
}

console.log(encodePosition(0, 4), encodePosition(1, 4))
plot.heatmap(range(32).map(p => encodePosition(p, 16)), { title: "positions 0–31 (rows) × 16 wave values (columns)" })
`,
  tests: `
test("position 0 is [0, 1, 0, 1]", () => expect(encodePosition(0, 4), "encodePosition(0, 4)").toEqual([0, 1, 0, 1]))
test("it has dims numbers", () => expect(encodePosition(5, 16), "encodePosition(5, 16)").toHaveLength(16))
test("the first wave is sin(pos)", () => expect(encodePosition(1, 4)[0], "encodePosition(1, 4)[0]").toBeCloseTo(Math.sin(1)))
test("the second pair is a slower wave", () => expect(encodePosition(1, 4)[2], "encodePosition(1, 4)[2]").toBeCloseTo(Math.sin(0.01)))
test("every value stays between −1 and 1", () => expect(encodePosition(123, 8).every(v => v >= -1 && v <= 1), "all in [−1, 1]").toBe(true))
test("different positions get different fingerprints", () => expect(encodePosition(3, 8).join() !== encodePosition(4, 8).join(), "3 vs 4 differ").toBe(true))
`,
}

// ─── 2.9 Project: growth ──────────────────────────────────────────────────────

export const growthSim: ExerciseDef = {
  starter: `// Part 1 — two ways to grow.
// linear(start, step, years): add "step" every year → [start, start + step, …] (years + 1 values)
function linear(start: number, step: number, years: number): number[] {
  return []
}

// compound(start, rate, years): multiply by (1 + rate) every year → years + 1 values.
// compound(100, 0.5, 2) → [100, 150, 225]
function compound(start: number, rate: number, years: number): number[] {
  return []
}

plot.line({ linear: linear(100, 50, 15), compound: compound(100, 0.25, 15) }, { title: "+50 a year vs. +25% a year" })
`,
  solution: `function linear(start: number, step: number, years: number): number[] {
  return range(years + 1).map(t => start + step * t)
}

function compound(start: number, rate: number, years: number): number[] {
  const out = [start]
  for (let t = 1; t <= years; t++) out.push(out[t - 1] * (1 + rate))
  return out
}

plot.line({ linear: linear(100, 50, 15), compound: compound(100, 0.25, 15) }, { title: "+50 a year vs. +25% a year" })
`,
  tests: `
test("linear(100, 50, 3) is [100, 150, 200, 250]", () => expect(linear(100, 50, 3), "linear(100, 50, 3)").toEqual([100, 150, 200, 250]))
test("compound(100, 0.5, 2) is [100, 150, 225]", () => expect(compound(100, 0.5, 2), "compound(100, 0.5, 2)").toEqual([100, 150, 225]))
test("compound has years + 1 values", () => expect(compound(1, 0.1, 10), "compound(1, 0.1, 10)").toHaveLength(11))
test("compound(1, 1, 10) ends at 1024", () => expect(compound(1, 1, 10)[10], "last value").toBeCloseTo(1024))
`,
}

export const doubling: ExerciseDef = {
  starter: `// Part 2 — how long until it doubles?
// We need t with (1 + rate)^t = 2. Take logs of both sides:  t · log(1 + rate) = log(2).
function doublingTime(rate: number): number {
  return 0
}

// yearsUntil(start, rate, target): the first whole number of years after which
// start · (1 + rate)^years is at least target. (Use logs, then Math.ceil.)
function yearsUntil(start: number, rate: number, target: number): number {
  return 0
}

console.log(doublingTime(0.07), 72 / 7)   // the "rule of 72" shortcut, for comparison
console.log(yearsUntil(100, 0.25, 10000))
`,
  solution: `function doublingTime(rate: number): number {
  return Math.log(2) / Math.log(1 + rate)
}

function yearsUntil(start: number, rate: number, target: number): number {
  return Math.ceil(Math.log(target / start) / Math.log(1 + rate))
}

console.log(doublingTime(0.07), 72 / 7)
console.log(yearsUntil(100, 0.25, 10000))
`,
  tests: `
test("doubling 100% a year takes 1 year", () => expect(doublingTime(1), "doublingTime(1)").toBeCloseTo(1))
test("7% a year doubles in about 10.24 years", () => expect(doublingTime(0.07), "doublingTime(0.07)").toBeCloseTo(10.2448, 1e-3))
test("100 → 10 000 at 25% takes 21 years", () => expect(yearsUntil(100, 0.25, 10000), "yearsUntil(100, 0.25, 10000)").toBe(21))
test("and it really is enough", () => expect(100 * 1.25 ** yearsUntil(100, 0.25, 10000), "value after that many years").toBeGreaterThan(9999.99))
test("one year fewer is not enough", () => expect(100 * 1.25 ** (yearsUntil(100, 0.25, 10000) - 1), "value a year earlier").toBeLessThan(10000))
`,
}

export const fitGrowth: ExerciseDef = {
  starter: `// Part 3 — read the growth rate off real-looking data.
// A (made-up) lab measured its yearly training compute, in petaFLOP-days:
const compute = [10.4, 29.1, 93.0, 262, 830, 2380, 7520, 21600]

// Exponential data turns into a straight line when you take logs. Look:
plot.line({ "log10(compute)": compute.map(Math.log10) }, { title: "a straight line on a log scale" })

// growthFactor(values): the average "times per year", using only the first and last values.
// If v_last = v_first · g^(n−1), then  g = (v_last / v_first)^(1 / (n − 1)).
function growthFactor(values: number[]): number {
  return 1
}

// predict(values, yearsAhead): continue the trend from the LAST value.
function predict(values: number[], yearsAhead: number): number {
  return 0
}

console.log(growthFactor(compute), predict(compute, 2))
`,
  solution: `const compute = [10.4, 29.1, 93.0, 262, 830, 2380, 7520, 21600]

plot.line({ "log10(compute)": compute.map(Math.log10) }, { title: "a straight line on a log scale" })

function growthFactor(values: number[]): number {
  const n = values.length
  return (values[n - 1] / values[0]) ** (1 / (n - 1))
}

function predict(values: number[], yearsAhead: number): number {
  return values[values.length - 1] * growthFactor(values) ** yearsAhead
}

console.log(growthFactor(compute), predict(compute, 2))
`,
  tests: `
test("[1, 2, 4, 8] grows ×2 a year", () => expect(growthFactor([1, 2, 4, 8]), "growthFactor([1, 2, 4, 8])").toBeCloseTo(2))
test("the lab's compute grows about ×3 a year", () => expect(growthFactor([10.4, 29.1, 93.0, 262, 830, 2380, 7520, 21600]), "growthFactor(compute)").toBeBetween(2.9, 3.1))
test("predict([1, 2, 4, 8], 2) is 32", () => expect(predict([1, 2, 4, 8], 2), "predict([1, 2, 4, 8], 2)").toBeCloseTo(32))
test("predict(…, 0) is the last value", () => expect(predict([5, 10], 0), "predict([5, 10], 0)").toBeCloseTo(10))
`,
}
